import { useEffect, useState } from 'react';
import {
  CasRenderBoolTrueFalse,
  CasRenderDate,
  CasRenderTruncatedText,
  CasTable,
} from 'src/compass/shared/components';
import prependAllOption from 'src/compass/shared/util/prependAllOption';
import { StringsToOptions } from 'src/compass/shared/util/StringsToOptions';
import { ICasTable } from 'src/compass/shared/components/CasTable/CasTable';
import ICasRenderCellProps from 'src/compass/shared/components/CasTable/ICasRenderCellProps';
import { CasYesNoSelect } from 'src/compass/application';
import { getDateInMoment } from 'src/compass/shared/util/casMoment';
import formatFwdContract from 'src/compass/shared/util/formatFwdContract';

import RenderInsuredName from './RenderInsuredName';
import { RenderLastActivity } from './RenderLastActivity';
import RenderDeadReports from './RenderDeadReports';
import RenderSharedClaimNumber from './RenderSharedClaimNumber';

import ClaimDetailsDialog from '../ClaimDetails/ClaimDetailsDialog';
import DeadReportsDialog from '../ClaimDetails/DeadReports/DeadReportsDialog';
import SharedClaimNumberDialog from '../ClaimDetails/SharedClaimNumberDialog';

type ClaimListingProps = ICasTable & {
  autoCheckout?: boolean;
  openDeadReports?: boolean;

  // Render Claim# as a yellow link opening the shared claim number dialog when the number is on
  // more than one claim.  Opt in: the list pays an extra query per page for the flag, so only the
  // tabs that show the link ask for it.
  linkSharedClaimNumber?: boolean;
}

export const CLAIM_STATUS_DONE_BG_COLOR = '#BFBFBF';

export enum ClaimStatus {
  Send = 'Send',
  DocusignInProcess = 'Docusign In Process',
  DocusignReview = 'Docusign Review',
  // A multi-SCE claim's SCE that has been reviewed and waits for its siblings before Attached.
  Reviewed = 'Reviewed',
  Approved = 'Approved',
  Attached = 'Attached',
  Callback = 'Callback',
  Done = 'Done',
}

export const ClaimStatusFilter_Empty = '(Empty)';
export const ClaimStatusFilter_ApprovedOrAttached = '(Approved or Attached)';
export const ClaimStatusFilter_NotDoneOrAttached = '(Not Done or Attached)';

const DAYS_LEFT_OPTIONS = prependAllOption(StringsToOptions(['1','2', '3','7', '10', '20', '50']));
const HAS_CLAIM_OPTIONS = prependAllOption([{id: false, name: 'No Claim'}, {id: true, name: 'Claim'}]);

export const CLAIM_STATUS_OPTIONS = prependAllOption(StringsToOptions([
  ClaimStatusFilter_ApprovedOrAttached,
  ClaimStatusFilter_Empty,
  '(Not Done)',
  ClaimStatusFilter_NotDoneOrAttached,
  ClaimStatus.Approved,
  ClaimStatus.Attached,
  ClaimStatus.Callback,
  ClaimStatus.Done,
  ClaimStatus.DocusignInProcess,
  ClaimStatus.DocusignReview,
  ClaimStatus.Reviewed,
  ClaimStatus.Send
]));

export const getColumnColorByReinsuranceYear = (row: Record<string, any>) => ({
  backgroundColor: row?.reinsuranceYearColor,
});

export const getColumnColorByDaysLeft = (row: Record<string, any>) => {
  const { daysLeft } = row;
  if (daysLeft === null) return {};
  if (daysLeft <= 10) return { backgroundColor: 'orange' };
  if (daysLeft <= 20) return { backgroundColor: 'yellow' };
  return {};
};

export const getColumnColorBySharedClaimNumber = (row: Record<string, any>) =>
  row?.hasSharedClaimNumber ? { backgroundColor: 'yellow' } : {};

export const getColumnColorByStatus = (row: Record<string, any>) => {
  const { status } = row;
  if (status === 'Done') return { backgroundColor: CLAIM_STATUS_DONE_BG_COLOR };
  if (status === 'Send') return { backgroundColor: '#FFE699' };
  if (status === 'Attached') return { backgroundColor: '#FF0066' };
  return {};
};

// NOTE: Property order will be used by SearchTab
export const ClaimColumns = {
  lastActivityDate: {
    field: 'lastActivityDate',
    label: 'Last Activity',
    render: RenderLastActivity,
  },
  insuredName: { field: 'insuredName' },
  reinsuranceYear: { field: 'reinsuranceYear', sx: getColumnColorByReinsuranceYear },
  policyNumber: { field: 'policyNumber', label: 'Policy#' },
  sce: { field: 'sce' },
  claimNumber: { field: 'claimNumber', label: 'Claim#' },
  status: { field: 'status', sx: getColumnColorByStatus },
  checkedOut: { field: 'checkedOut', sortable: false },
  effectiveDate: { field: 'effectiveDate', render: CasRenderDate },
  endDate: { field: 'endDate', render: CasRenderDate },
  deadsReported: { field: 'deadsReported', render: RenderDeadReports },
  claim: { field: 'claim', label: 'Claim?', render: CasRenderBoolTrueFalse },
  daysLeft: { 
    field: 'claimDueByDate',
    label: 'Days Left',
    render: (props: ICasRenderCellProps) => props.row.daysLeft,
    sx: getColumnColorByDaysLeft,
  },
  isFwdContract: {
    field: 'isFwdContract',
    // The cell shows the date, so sort on the date rather than the 0/1 flag.
    id: 'forwardContractDate',
    label: 'Is Fwd Contract?',
    render: (props: ICasRenderCellProps) =>
      formatFwdContract(props.row.isFwdContract, props.row.forwardContractDate),
  },
  noticeSentDate: { field: 'noticeSentDate', label: 'Notice Sent', render: CasRenderDate },
  claimReceivedDate: { field: 'claimReceivedDate', label: 'Received', render: CasRenderDate },
  claimAttachedDate: { field: 'claimAttachedDate', label: 'Attached', render: CasRenderDate },
  claimRejectedDate: { field: 'claimRejectedDate', label: 'Rejected', render: CasRenderDate },
  claimVerifiedDate: { field: 'claimVerifiedDate', label: 'Reviewed', render: CasRenderDate },
  claimNotes: { field: 'claimNotes', render: CasRenderTruncatedText },
  whatSent: { field: 'whatSent', label: 'What' },
  assigned: { field: 'assigned', render: CasRenderDate },
  callbackType: { field: 'callbackType' },
  assignedTo: { field: 'assignedTo' },
  callbackNotes: { field: 'callbackNotes', render: CasRenderTruncatedText  },
  state: { field: 'state' },
  county: { field: 'county' },
  crop: { field: 'crop' },
  type: { field: 'type' },
  hc: { field: 'totalNumberInsured', label: 'HC' },
  targetCwt: { field: 'targetCWT', label: 'Target Cwt' },
  coveragePrice: { field: 'coveragePrice' },
};
/*
  NOTE: Property order will be used by SearchTab
  SearchTab puts these in CasTable's "More" drawer (`filtersInDrawer`), and the `isPrimaryFilter`
  ones - everything up to Claim# - also stay on its toolbar.  The other tabs leave the drawer off,
  where `isPrimaryFilter` does nothing.
*/
export const ClaimFilters = {
  insuredNameSearch: {
    name: 'insuredNameSearch',
    label: 'Insured search',
    isPrimaryFilter: true,
    autoFocus: true
  },
  policyNumber: {
    type: 'number',
    name: 'policyNumber',
    label: 'Policy#',
    isPrimaryFilter: true,
  },
  sce: {
    type: 'number',
    name: 'sce',
    isPrimaryFilter: true,
  },
  claimNumber: {
    type: 'number',
    name: 'claimNumber',
    label: 'Claim#',
    isPrimaryFilter: true,
  },
  daysLeftLessThan: {
    type: 'select',
    name: 'daysLeftLessThan',
    label: 'Days Left <=',
    options: DAYS_LEFT_OPTIONS,
  },
  hasClaim: {
    type: 'select',
    name: 'hasClaim',
    label: 'Has Claim?',
    options: HAS_CLAIM_OPTIONS,
  },
  status: {
    type: 'select',
    name: 'status',
    options: CLAIM_STATUS_OPTIONS,
  },
  isFwdContract: {
    type: 'component',
    name: 'isFwdContract',
    label: 'Is Fwd Contract?',
    includeAllOption: true,
    component: CasYesNoSelect,
  },
  endDate: {
    type: 'date-range',
    name: 'endDate',
    componentProps: {
      shortcuts: [
        {
          label: 'This Week',
          start: getDateInMoment().startOf('week'),
          end: getDateInMoment().endOf('week'),
        },
        {
          label: 'Last Week',
          start: getDateInMoment().subtract(1, 'week').startOf('week'),
          end: getDateInMoment().subtract(1, 'week').endOf('week'),
        },
        {
          label: 'Last 7 Days',
          start: getDateInMoment().subtract(6, 'days').startOf('day'),
          end: getDateInMoment().endOf('day'),
        },
        {
          label: 'Current Month',
          start: getDateInMoment().startOf('month'),
          end: getDateInMoment().endOf('month'),
        },
        {
          label: 'Next Month',
          start: getDateInMoment().add(1, 'month').startOf('month'),
          end: getDateInMoment().add(1, 'month').endOf('month'),
        },
      ],
    }
  },
  insuredName: {
    type: 'autocomplete',
    name: 'insuredName',
    url: 'Sce/InsuredName/Select/List',
  },
  rejected: {
    type: 'component',
    name: 'rejected',
    includeAllOption: true,
    component: CasYesNoSelect,
  },
};

export default function ClaimListing({
  columns: paramColumns,
  autoCheckout=true,
  openDeadReports: parmOpenDeadReports = false,
  linkSharedClaimNumber = false,
  // Pulled out of ...rest so the payload below can merge into it - ...rest spreads last.
  additionalPayload,
  // Pulled out too: a parent's reload (after its own dialog changed a claim) adds to this
  // listing's own reloads rather than replacing them.
  update,
  ...rest
}: ClaimListingProps) {
  const [dialogRowId, setDialogRowId] = useState<any>(null);
  const [sharedClaimNumber, setSharedClaimNumber] = useState<number | null>(null);
  const [reloadTable, setReloadTable] = useState<Date | null>(null);
  const [openDeadReports, setOpenDeadReports] = useState<boolean>(parmOpenDeadReports);

  useEffect(() => {
    if (update) setReloadTable(new Date());
  }, [update]);

  const adjustedColumns = paramColumns.map((col) => {
    if (col.field === 'insuredName')
      return {
        ...col,
        render: (props: ICasRenderCellProps) => (
          <RenderInsuredName onClick={() => setDialogRowId(props.row.id)} {...props} />
        ),
      };

    if (linkSharedClaimNumber && col.field === 'claimNumber')
      return {
        ...col,
        sx: getColumnColorBySharedClaimNumber,
        render: (props: ICasRenderCellProps) => (
          <RenderSharedClaimNumber
            onClick={() => setSharedClaimNumber(props.row.claimNumber)}
            {...props}
          />
        ),
      };

    return col;
  });

  const payload = linkSharedClaimNumber
    ? { ...additionalPayload, loadHasSharedClaimNumber: true }
    : additionalPayload;

  return (
    <>
      <CasTable
        entity="Sce/Claims"
        update={reloadTable}
        columns={adjustedColumns}
        additionalPayload={payload}
        {...rest}
      />

      {sharedClaimNumber !== null && (
        <SharedClaimNumberDialog
          claimNumber={sharedClaimNumber}
          autoCheckout={autoCheckout}
          onClose={() => {
            setSharedClaimNumber(null);
            // Its rows open their own details, which can change a claim.
            setReloadTable(new Date());
          }}
        />
      )}

      {dialogRowId &&
        (openDeadReports ? (
          <DeadReportsDialog
            id={dialogRowId}
            onClose={() => {
              setDialogRowId(null);
              setReloadTable(new Date());
            }}
            onViewClaimDetails={() => setOpenDeadReports(false)}
          />
        ) : (
          <ClaimDetailsDialog
            autoCheckout={autoCheckout}
            id={dialogRowId}
            onClose={() => {
              setDialogRowId(null);
              setReloadTable(new Date());
              setOpenDeadReports(parmOpenDeadReports);
            }}
          />
        ))}
    </>
  );
}
